/**
 * XChaCha20-Poly1305 IETF AEAD, backed by libsodium
 * (`crypto_aead_xchacha20poly1305_ietf_*`). Node's `crypto` has no XChaCha20
 * variant, so this primitive is the primary justification for the libsodium
 * dependency.
 *
 * The `ciphertext` field is `ciphertext || tag` (16-byte Poly1305 tag appended).
 */
import { sodium } from './backend.js';
import { FumeCryptoError } from './errors.js';
import { XCHACHA20POLY1305_KEY_SIZE, XCHACHA20POLY1305_NONCE_SIZE, XCHACHA20POLY1305_TAG_SIZE, } from './constants.js';
import { requireLength, toUint8Array } from './internal.js';
/**
 * Encrypt `plaintext` under `key` with associated data `aad`. If `nonce` is
 * omitted, a fresh random 24-byte nonce is generated (XChaCha20's 192-bit nonce
 * space makes random nonces safe).
 */
export function xchacha20poly1305Encrypt(plaintext, aad, key, nonce) {
    if (!(plaintext instanceof Uint8Array)) {
        throw new FumeCryptoError('plaintext must be a Uint8Array');
    }
    if (!(aad instanceof Uint8Array)) {
        throw new FumeCryptoError('aad must be a Uint8Array');
    }
    requireLength(key, XCHACHA20POLY1305_KEY_SIZE, 'key');
    let nonceBytes;
    if (nonce === undefined) {
        nonceBytes = sodium.randombytes_buf(XCHACHA20POLY1305_NONCE_SIZE);
    }
    else {
        requireLength(nonce, XCHACHA20POLY1305_NONCE_SIZE, 'nonce');
        nonceBytes = nonce;
    }
    const ciphertext = sodium.crypto_aead_xchacha20poly1305_ietf_encrypt(plaintext, aad, null, nonceBytes, key);
    return { ciphertext: toUint8Array(ciphertext), nonce: toUint8Array(nonceBytes) };
}
/**
 * Decrypt and authenticate `ciphertext` (which must include the trailing tag).
 * Throws {@link FumeCryptoError} on any tampering, AD mismatch, or bad nonce/key.
 */
export function xchacha20poly1305Decrypt(ciphertext, aad, key, nonce) {
    if (!(ciphertext instanceof Uint8Array)) {
        throw new FumeCryptoError('ciphertext must be a Uint8Array');
    }
    if (!(aad instanceof Uint8Array)) {
        throw new FumeCryptoError('aad must be a Uint8Array');
    }
    requireLength(key, XCHACHA20POLY1305_KEY_SIZE, 'key');
    requireLength(nonce, XCHACHA20POLY1305_NONCE_SIZE, 'nonce');
    if (ciphertext.length < XCHACHA20POLY1305_TAG_SIZE) {
        throw new FumeCryptoError('ciphertext is shorter than the authentication tag');
    }
    try {
        const message = sodium.crypto_aead_xchacha20poly1305_ietf_decrypt(null, ciphertext, aad, nonce, key);
        return toUint8Array(message);
    }
    catch (err) {
        throw new FumeCryptoError(`AEAD decryption failed (tampered ciphertext, wrong AAD, nonce, or key): ${err.message}`);
    }
}
//# sourceMappingURL=aead.js.map